<template>
  <div>
    <v-card outlined class="pa-4">
      <!-- ENCABEZADO -->
      <ReportHeader
        :titulo-reporte="tituloReporte"
        :fecha-reporte="fechaReporte"
        :has-data="!!(resultados && resultados.length > 0)"
        :is-exporting="isExporting"
        :is-exporting-excel="isExportingExcel"
        :is-exporting-in-background="isExportingInBackground"
        :is-exporting-pdf-in-background="isExportingPdfInBackground"
        @export-excel="handleExportExcel"
        @export-pdf="handleExportPDF"
      />

      <!-- TABLA -->
      <ReportTable
        :resultados="resultados"
        :total-items="totalItems"
        :items-por-pagina="itemsPorPagina"
        :pagina-actual="paginaActual"
        :loading="loading"
        :headers="headers"
        :es-operacion="esOperacion"
        :totales="totales"
        @cambiar-pagina="onCambiarPagina"
        @cambiar-items-por-pagina="onCambiarItemsPorPagina"
      />
    </v-card>
  </div>
</template>

<script>
import ReportHeader from './ReportHeader.vue';
import ReportTable from './ReportTable.vue';
import ReportCalculations from './ReportCalculations';
import ReportExportMixin from './ReportExport/ReportExportMixin';  // ✅ Nueva arquitectura modular
import InfoCobranzasExport from './InfoCobranzasExport';

export default {
  name: 'TablaReportes',
  components: {
    ReportHeader,
    ReportTable,
  },
  mixins: [ReportCalculations, InfoCobranzasExport, ReportExportMixin],  // ✅ Usando nueva arquitectura modular
  props: {
    resultados: {
      type: Array,
      default: () => [],
    },
    fechaReporte: {
      type: String,
      default: () => new Date().toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }),
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    itemsPorPagina: {
      type: Number,
      default: 10,
    },
    paginaActual: {
      type: Number,
      default: 1,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isExporting: {
      type: Boolean,
      default: false,
    },
    isExportingExcel: {
      type: Boolean,
      default: false,
    },
    isExportingInBackground: {
      type: Boolean,
      default: false,
    },
    isExportingPdfInBackground: {
      type: Boolean,
      default: false,
    },
    headers: {
      type: Array,
      default: () => [
        { text: 'Legajo', value: 'id' },
        { text: 'Fideicomiso', value: 'fideicomiso' },
        { text: 'Operatoria / Línea', value: 'operatoria' },
        { text: 'Apellido y Nombre / Razón Social', value: 'cliente' },
        { text: 'CUIT', value: 'cuit' },
        { text: 'Localidad', value: 'localidad' },
        { text: 'Persona', value: 'persona' },
        { text: 'Monto ANR Solicitado', value: 'monto_total_solicitado' },
        { text: 'Monto Aprobado', value: 'monto_aprobado' },
        { text: 'Fecha de Ingreso', value: 'fechaIngreso' },
        { text: 'Estado', value: 'estado' },
        { text: 'Monto Solicitado', value: 'monto' },
      ],
    },
    esOperacion: {
      type: String,
    },
    totales: {
      type: Object,
      default: () => ({}),
    },
    fechaInicio: {
      type: String,
      default: '',
    },
    fechaFinal: {
      type: String,
      default: '',
    },
    periodoSaldosHtc: {
      type: String,
      default: null,
    },
  },
  emits: ['cambiar-pagina', 'cambiar-items-por-pagina', 'exportar-datos'],
  computed: {
    // Título dinámico según el tipo de reporte
    tituloReporte() {
      switch (this.esOperacion) {
        case 'ANR':
        case 'Anr':
          return 'INFORME DE ANR';
        case 'Info Mora (Financiamiento)':
          return 'INFORME DE MORA';
        case 'Info Cobranzas Proyectadas':
          return 'INFORME DE COBRANZAS PROYECTADAS';
        case 'Info Cobranzas':
          return 'INFORME DE COBRANZAS';
        case 'Info Cte y No Cte':
          return 'INFORME DE CRÉDITOS CORRIENTES Y NO CORRIENTES';
        case 'Reporte de Saldos - HTC':
          return 'REPORTE DE SALDOS - HTC';
        case 'Financiamiento':
          return 'INFORME DE FINANCIAMIENTO';
        default:
          return 'INFORME DE SOLICITUDES';
      }
    },

    // Cantidad máxima de desembolsos con datos reales (requerido por nueva arquitectura)
    maxDesembolsos() {
      if (!this.resultados || this.resultados.length === 0) return 0;

      let maxConDatos = 0;

      // Recorrer todos los items para encontrar el desembolso más alto que tenga datos
      this.resultados.forEach(item => {
        if (item._esSubtotal || !item.desembolsos || !Array.isArray(item.desembolsos)) return;

        // Para cada desembolso, verificar si tiene datos reales (monto > 0 o fecha)
        item.desembolsos.forEach(desembolso => {
          const tieneMonto = desembolso.monto && parseFloat(desembolso.monto) > 0;
          const tieneFecha = desembolso.fecha_desembolso && desembolso.fecha_desembolso !== '-';

          if (tieneMonto || tieneFecha) {
            const numDesembolso = desembolso.numeroDesembolso || 0;
            maxConDatos = Math.max(maxConDatos, numDesembolso);
          }
        });
      });

      console.log(`📊 Desembolsos con datos reales: ${maxConDatos}`);
      return maxConDatos;
    },

    headersConDesembolsos() {
      const baseHeaders = [...this.headers];

      // Agregar columnas dinámicas de desembolsos
      for (let i = 1; i <= this.maxDesembolsos; i++) {
        if (this.esOperacion === 'ANR') {
          baseHeaders.push({
            text: `Desembolso ${i}`,
            value: `desembolso_${i}`,
            sortable: false,
          });
          baseHeaders.push({
            text: `Fecha Desembolso ${i}`,
            value: `fecha_desembolso_${i}`,
            sortable: false,
          });
        } else if (this.esOperacion === 'Financiamiento') {
          baseHeaders.push({
            text: `Desembolso ${i}`,
            value: `desembolso_${i}`,
            sortable: false,
          });
          baseHeaders.push({
            text: `Fecha Desembolso ${i}`,
            value: `fecha_desembolso_${i}`,
            sortable: false,
          });
        }
      }

      // Agregar columna de monto pendiente al final solo si es operación ANR
      if (this.esOperacion === 'ANR') {
        baseHeaders.push({
          text: 'Monto Pend./Desembolso',
          value: 'monto_pendiende',
          sortable: false,
        });
      }

      // Agregar columnas de gastos para Financiamiento
      if (this.esOperacion === 'Financiamiento') {
        const gastosHeaders = [
          { text: 'Honorarios MF', value: 'honorarios_mf', sortable: false },
          { text: 'Gastos de Escribano', value: 'gastos_escribano', sortable: false },
          { text: 'Otros Gastos', value: 'otros_gastos', sortable: false },
          { text: 'Monto Liquidado', value: 'liquidado', sortable: false }
        ];

        gastosHeaders.forEach(gastoHeader => {
          if (!baseHeaders.some(h => h.value === gastoHeader.value)) {
            baseHeaders.push(gastoHeader);
          }
        });
      }

      return baseHeaders;
    },
  },
  methods: {
    onCambiarPagina(page) {
      this.$emit('cambiar-pagina', page);
    },
    onCambiarItemsPorPagina(itemsPerPage) {
      this.$emit('cambiar-items-por-pagina', itemsPerPage);
    },

    // Handlers de botones - Solo emiten eventos al padre, NO exportan directamente
    handleExportExcel() {
      console.log('📤 handleExportExcel - Emitiendo evento al padre');
      this.$emit('exportar-datos', 'excel');
    },

    handleExportPDF() {
      console.log('📤 handleExportPDF - Emitiendo evento al padre');
      this.$emit('exportar-datos', 'pdf');
    },

    // ✅ NUEVA ARQUITECTURA MODULAR
    // Métodos llamados por el PADRE con todos los datos
    generarPDF(todosLosDatos) {
      console.log('🔄 generarPDF - Ejecutando exportación con', todosLosDatos.length, 'registros');
      return this.exportarPDF(todosLosDatos);
    },

    generarExcel(todosLosDatos) {
      console.log('🔄 generarExcel - Ejecutando exportación con', todosLosDatos.length, 'registros');
      return this.exportarExcel(todosLosDatos);
    },
  },
};
</script>

<style>
* {
  box-sizing: border-box;
}

.v-card {
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  font-family: 'Roboto', sans-serif;
}

p,
li {
  font-size: 14px;
  color: #333;
  margin-bottom: 4px;
}

ul {
  padding-left: 18px;
  margin: 6px 0;
}
</style>